// Onboarding (wizard de 5 passos) e checklist da primeira promocao.
// Leitura: onboarding_progress (RLS) e get_setup_status (RPC). Escrita so via
// RPC (save_onboarding_progress, dismiss_setup_checklist). Transicoes puras em
// @lucralink/shared/onboarding.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  INITIAL_ONBOARDING_PROGRESS,
  normalizeOnboardingProgress,
  normalizeSetupStatus,
  type OnboardingProgress,
  type SetupStatus,
} from "@lucralink/shared";
import { supabase } from "@/lib/supabase";

export const onboardingKeys = {
  progress: ["onboarding-progress"] as const,
  setup: ["setup-status"] as const,
};

export interface OnboardingState {
  /** null = conta sem linha (nao mostra wizard). */
  readonly progress: OnboardingProgress | null;
  readonly checklistDismissed: boolean;
}

async function fetchOnboarding(): Promise<OnboardingState> {
  const { data, error } = await supabase.from("onboarding_progress").select("*").limit(1).maybeSingle();
  if (error) throw error;
  const row = data as Record<string, unknown> | null;
  return {
    progress: normalizeOnboardingProgress(row),
    checklistDismissed: Boolean(row?.["checklist_dismissed_at"]),
  };
}

export function useOnboardingState() {
  return useQuery<OnboardingState, Error>({
    queryKey: onboardingKeys.progress,
    queryFn: fetchOnboarding,
    staleTime: 60_000,
  });
}

/** Grava o progresso e ja atualiza o cache (o wizard nao pisca entre passos). */
export function useSaveOnboardingProgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (progress: OnboardingProgress) => {
      const { error } = await supabase.rpc("save_onboarding_progress", {
        p_current_step: progress.currentStep,
        p_completed_steps: progress.completedSteps,
        p_skipped_steps: progress.skippedSteps,
        p_selected_platforms: progress.selectedPlatforms,
        p_finished: progress.finished,
      });
      if (error) throw error;
      return progress;
    },
    onSuccess: (progress) => {
      qc.setQueryData<OnboardingState>(onboardingKeys.progress, (prev) => ({
        checklistDismissed: prev?.checklistDismissed ?? false,
        progress,
      }));
    },
  });
}

/** "Refazer configuracao": volta o wizard ao primeiro passo. */
export function useRestartOnboarding() {
  const save = useSaveOnboardingProgress();
  return { ...save, restart: () => save.mutateAsync(INITIAL_ONBOARDING_PROGRESS) };
}

async function fetchSetupStatus(): Promise<SetupStatus> {
  const { data, error } = await supabase.rpc("get_setup_status");
  if (error) throw error;
  return normalizeSetupStatus(data);
}

export function useSetupStatus(enabled = true) {
  return useQuery<SetupStatus, Error>({
    queryKey: onboardingKeys.setup,
    queryFn: fetchSetupStatus,
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useDismissChecklist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("dismiss_setup_checklist");
      if (error) throw error;
    },
    onSuccess: () => {
      qc.setQueryData<OnboardingState>(onboardingKeys.progress, (prev) => ({
        progress: prev?.progress ?? null,
        checklistDismissed: true,
      }));
    },
  });
}
